import { useId, type ReactNode } from 'react';
import {
  GENERATION_ASPECTS,
  GENERATION_DIALECTS,
  type GenerationFollowUp,
  type GenerationGoal,
  type GenerationOptions,
} from '@mpr/shared';

import { diacriticsApply, relevantGroups, type OptionGroup } from '../lib/studioGoal.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import type { TranslationKey } from '../i18n/translations.js';

const QUALITIES = ['DRAFT', 'STANDARD', 'HIGH'] as const;
const DIACRITICS = ['NONE', 'PARTIAL', 'FULL'] as const;
const TEXT_MODES = ['NONE', 'EXACT', 'TITLE'] as const;
const POST_LENGTHS = ['SHORT', 'MEDIUM', 'LONG'] as const;
const POST_TONES = ['FORMAL', 'FRIENDLY', 'EXCITED', 'PERSUASIVE', 'INFORMATIVE'] as const;
const TRANSITIONS = ['AUTO', 'CUT', 'FADE', 'DISSOLVE'] as const;
const PACES = ['SLOW', 'NORMAL', 'FAST'] as const;
const SOUNDS = ['NONE', 'AMBIENT', 'MUSIC', 'AMBIENT_MUSIC'] as const;
const GENDERS = ['ANY', 'MALE', 'FEMALE'] as const;
const AUDIO_TONES = ['NEUTRAL', 'WARM', 'ENERGETIC', 'CALM', 'SERIOUS'] as const;

const GROUP_TITLE: Record<OptionGroup, TranslationKey> = {
  language: 'studio.opt.group.language',
  textOnMedia: 'studio.opt.group.textOnMedia',
  post: 'studio.opt.group.post',
  image: 'studio.opt.group.image',
  video: 'studio.opt.group.video',
  audio: 'studio.opt.group.audio',
};

/** A control with its label above and its one-line explanation beneath. */
function Field({ id, label, hint, children }: { id: string; label: string; hint: string; children: ReactNode }) {
  return (
    <div className="goal-field">
      <label htmlFor={id}>{label}</label>
      {children}
      <p className="muted tiny goal-hint">{hint}</p>
    </div>
  );
}

function SelectField<T extends string>({
  id,
  label,
  hint,
  value,
  choices,
  labelOf,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  value: T | undefined;
  choices: readonly T[];
  labelOf: (choice: T) => string;
  onChange: (value: T) => void;
}) {
  return (
    <Field id={id} label={label} hint={hint}>
      <select id={id} value={value ?? ''} onChange={(event) => onChange(event.target.value as T)}>
        {value === undefined && <option value="" />}
        {choices.map((choice) => (
          <option key={choice} value={choice}>
            {labelOf(choice)}
          </option>
        ))}
      </select>
    </Field>
  );
}

function CheckField({
  id,
  label,
  hint,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="goal-field">
      <label className="check-row" htmlFor={id}>
        <input id={id} type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
        <span>{label}</span>
      </label>
      <p className="muted tiny goal-hint">{hint}</p>
    </div>
  );
}

function NumberField({
  id,
  label,
  hint,
  value,
  min,
  max,
  step,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  value: number | undefined;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number | undefined) => void;
}) {
  return (
    <Field id={id} label={label} hint={hint}>
      <input
        id={id}
        type="number"
        className="ltr"
        dir="ltr"
        inputMode="decimal"
        min={min}
        max={max}
        step={step ?? 1}
        value={value ?? ''}
        onChange={(event) => {
          const parsed = event.target.value === '' ? undefined : Number(event.target.value);
          onChange(parsed === undefined || Number.isNaN(parsed) ? undefined : parsed);
        }}
      />
    </Field>
  );
}

/**
 * The choices for a request, in collapsible groups. Only the groups that
 * apply to the goal (and a post's follow-up) are shown, so a person asking
 * for a post never sees "scene continuity" and a person asking for a video
 * never has to wonder what "hashtags" is doing there.
 *
 * `value` is always the complete set of choices (the form starts from the
 * documented defaults); each control changes one key and hands the whole
 * object back.
 */
export function OptionsPanel({
  goal,
  followUp,
  value,
  onChange,
}: {
  goal: GenerationGoal;
  followUp: GenerationFollowUp;
  value: GenerationOptions;
  onChange: (next: GenerationOptions) => void;
}) {
  const { t } = useI18n();
  const uid = useId();
  const id = (name: string) => `opt-${name}-${uid}`;
  const tk = (key: string) => t(key as TranslationKey);

  const groups = relevantGroups(goal, followUp);
  const post = value.post ?? {};
  const image = value.image ?? {};
  const video = value.video ?? {};
  const audio = value.audio ?? {};
  const textOnMedia = value.textOnMedia ?? { mode: 'NONE' as const };

  const setPost = (patch: Partial<NonNullable<GenerationOptions['post']>>) =>
    onChange({ ...value, post: { ...post, ...patch } });
  const setImage = (patch: Partial<NonNullable<GenerationOptions['image']>>) =>
    onChange({ ...value, image: { ...image, ...patch } });
  const setVideo = (patch: Partial<NonNullable<GenerationOptions['video']>>) =>
    onChange({ ...value, video: { ...video, ...patch } });
  const setAudio = (patch: Partial<NonNullable<GenerationOptions['audio']>>) =>
    onChange({ ...value, audio: { ...audio, ...patch } });

  const aspectField = (
    name: 'image' | 'video',
    current: GenerationOptions['image'] | GenerationOptions['video'],
    set: (patch: { aspect: (typeof GENERATION_ASPECTS)[number] }) => void,
  ) => (
    <SelectField
      id={id(`${name}-aspect`)}
      label={t('studio.opt.aspect')}
      hint={t('studio.opt.aspect.hint')}
      value={current?.aspect}
      choices={GENERATION_ASPECTS}
      labelOf={(a) => tk(`studio.opt.aspect.${a}`)}
      onChange={(aspect) => set({ aspect })}
    />
  );

  const qualityField = (
    name: 'image' | 'video',
    current: GenerationOptions['image'] | GenerationOptions['video'],
    set: (patch: { quality: (typeof QUALITIES)[number] }) => void,
  ) => (
    <SelectField
      id={id(`${name}-quality`)}
      label={t('studio.opt.quality')}
      hint={t('studio.opt.quality.hint')}
      value={current?.quality}
      choices={QUALITIES}
      labelOf={(q) => tk(`studio.opt.quality.${q}`)}
      onChange={(quality) => set({ quality })}
    />
  );

  const body: Record<OptionGroup, ReactNode> = {
    language: (
      <>
        <SelectField
          id={id('language')}
          label={t('studio.opt.language')}
          hint={t('studio.opt.language.hint')}
          value={value.language}
          choices={['ar', 'en'] as const}
          labelOf={(l) => tk(`studio.opt.language.${l}`)}
          onChange={(language) => onChange({ ...value, language })}
        />
        {value.language !== 'en' && (
          <SelectField
            id={id('dialect')}
            label={t('studio.opt.dialect')}
            hint={t('studio.opt.dialect.hint')}
            value={value.dialect}
            choices={GENERATION_DIALECTS}
            labelOf={(d) => tk(`studio.opt.dialect.${d}`)}
            onChange={(dialect) => onChange({ ...value, dialect })}
          />
        )}
        {diacriticsApply(goal, followUp) && value.language !== 'en' && (
          <SelectField
            id={id('diacritics')}
            label={t('studio.opt.diacritics')}
            hint={t('studio.opt.diacritics.hint')}
            value={value.diacritics}
            choices={DIACRITICS}
            labelOf={(d) => tk(`studio.opt.diacritics.${d}`)}
            onChange={(diacritics) => onChange({ ...value, diacritics })}
          />
        )}
        <Field id={id('extra')} label={t('studio.opt.extra')} hint={t('studio.opt.extra.hint')}>
          <textarea
            id={id('extra')}
            dir="auto"
            rows={2}
            maxLength={4000}
            value={value.extraInstructions ?? ''}
            onChange={(event) => onChange({ ...value, extraInstructions: event.target.value })}
          />
        </Field>
      </>
    ),

    textOnMedia: (
      <>
        <SelectField
          id={id('text-mode')}
          label={t('studio.opt.textOnMedia')}
          hint={t('studio.opt.textOnMedia.hint')}
          value={textOnMedia.mode}
          choices={TEXT_MODES}
          labelOf={(m) => tk(`studio.opt.textOnMedia.${m}`)}
          onChange={(mode) => onChange({ ...value, textOnMedia: { ...textOnMedia, mode } })}
        />
        {textOnMedia.mode === 'EXACT' && (
          <Field
            id={id('text-words')}
            label={t('studio.opt.textOnMedia.text')}
            hint={t('studio.opt.textOnMedia.text.hint')}
          >
            <input
              id={id('text-words')}
              dir="auto"
              maxLength={500}
              value={textOnMedia.text ?? ''}
              onChange={(event) =>
                onChange({ ...value, textOnMedia: { ...textOnMedia, text: event.target.value } })
              }
            />
          </Field>
        )}
      </>
    ),

    post: (
      <>
        <SelectField
          id={id('post-length')}
          label={t('studio.opt.post.length')}
          hint={t('studio.opt.post.length.hint')}
          value={post.length}
          choices={POST_LENGTHS}
          labelOf={(l) => tk(`studio.opt.post.length.${l}`)}
          onChange={(length) => setPost({ length })}
        />
        <SelectField
          id={id('post-tone')}
          label={t('studio.opt.post.tone')}
          hint={t('studio.opt.post.tone.hint')}
          value={post.tone}
          choices={POST_TONES}
          labelOf={(l) => tk(`studio.opt.post.tone.${l}`)}
          onChange={(tone) => setPost({ tone })}
        />
        <CheckField
          id={id('post-hashtags')}
          label={t('studio.opt.post.hashtags')}
          hint={t('studio.opt.post.hashtags.hint')}
          checked={post.hashtags ?? false}
          onChange={(hashtags) => setPost({ hashtags })}
        />
        <CheckField
          id={id('post-emojis')}
          label={t('studio.opt.post.emojis')}
          hint={t('studio.opt.post.emojis.hint')}
          checked={post.emojis ?? false}
          onChange={(emojis) => setPost({ emojis })}
        />
        <CheckField
          id={id('post-cta')}
          label={t('studio.opt.post.callToAction')}
          hint={t('studio.opt.post.callToAction.hint')}
          checked={post.callToAction ?? false}
          onChange={(callToAction) => setPost({ callToAction })}
        />
      </>
    ),

    image: (
      <>
        {aspectField('image', image, setImage)}
        {qualityField('image', image, setImage)}
        <Field id={id('image-style')} label={t('studio.opt.image.style')} hint={t('studio.opt.image.style.hint')}>
          <input
            id={id('image-style')}
            dir="auto"
            maxLength={300}
            value={image.style ?? ''}
            onChange={(event) => setImage({ style: event.target.value })}
          />
        </Field>
      </>
    ),

    video: (
      <>
        {aspectField('video', video, setVideo)}
        {qualityField('video', video, setVideo)}
        <NumberField
          id={id('video-duration')}
          label={t('studio.opt.video.duration')}
          hint={t('studio.opt.video.duration.hint')}
          value={video.durationSeconds}
          min={2}
          max={180}
          onChange={(durationSeconds) => setVideo({ durationSeconds })}
        />
        <NumberField
          id={id('video-scenes')}
          label={t('studio.opt.video.scenes')}
          hint={t('studio.opt.video.scenes.hint')}
          value={video.scenes}
          min={1}
          max={30}
          onChange={(scenes) => setVideo({ scenes })}
        />
        <CheckField
          id={id('video-character')}
          label={t('studio.opt.video.characterConsistency')}
          hint={t('studio.opt.video.characterConsistency.hint')}
          checked={video.characterConsistency ?? false}
          onChange={(characterConsistency) => setVideo({ characterConsistency })}
        />
        <CheckField
          id={id('video-continuity')}
          label={t('studio.opt.video.sceneContinuity')}
          hint={t('studio.opt.video.sceneContinuity.hint')}
          checked={video.sceneContinuity ?? false}
          onChange={(sceneContinuity) => setVideo({ sceneContinuity })}
        />
        <SelectField
          id={id('video-transitions')}
          label={t('studio.opt.video.transitions')}
          hint={t('studio.opt.video.transitions.hint')}
          value={video.transitions}
          choices={TRANSITIONS}
          labelOf={(v) => tk(`studio.opt.video.transitions.${v}`)}
          onChange={(transitions) => setVideo({ transitions })}
        />
        <SelectField
          id={id('video-pace')}
          label={t('studio.opt.video.pace')}
          hint={t('studio.opt.video.pace.hint')}
          value={video.pace}
          choices={PACES}
          labelOf={(v) => tk(`studio.opt.video.pace.${v}`)}
          onChange={(pace) => setVideo({ pace })}
        />
        <CheckField
          id={id('video-order')}
          label={t('studio.opt.video.keepScriptOrder')}
          hint={t('studio.opt.video.keepScriptOrder.hint')}
          checked={video.keepScriptOrder ?? false}
          onChange={(keepScriptOrder) => setVideo({ keepScriptOrder })}
        />
        <SelectField
          id={id('video-sound')}
          label={t('studio.opt.video.sound')}
          hint={t('studio.opt.video.sound.hint')}
          value={video.sound}
          choices={SOUNDS}
          labelOf={(v) => tk(`studio.opt.video.sound.${v}`)}
          onChange={(sound) => setVideo({ sound })}
        />
      </>
    ),

    audio: (
      <>
        <SelectField
          id={id('audio-gender')}
          label={t('studio.opt.audio.gender')}
          hint={t('studio.opt.audio.gender.hint')}
          value={audio.gender}
          choices={GENDERS}
          labelOf={(v) => tk(`studio.opt.audio.gender.${v}`)}
          onChange={(gender) => setAudio({ gender })}
        />
        <Field id={id('audio-voice')} label={t('studio.opt.audio.voice')} hint={t('studio.opt.audio.voice.hint')}>
          <input
            id={id('audio-voice')}
            className="ltr"
            dir="ltr"
            maxLength={200}
            value={audio.voice ?? ''}
            onChange={(event) => setAudio({ voice: event.target.value })}
          />
        </Field>
        <NumberField
          id={id('audio-speed')}
          label={t('studio.opt.audio.speed')}
          hint={t('studio.opt.audio.speed.hint')}
          value={audio.speed}
          min={0.5}
          max={2}
          step={0.1}
          onChange={(speed) => setAudio({ speed })}
        />
        <SelectField
          id={id('audio-tone')}
          label={t('studio.opt.audio.tone')}
          hint={t('studio.opt.audio.tone.hint')}
          value={audio.tone}
          choices={AUDIO_TONES}
          labelOf={(v) => tk(`studio.opt.audio.tone.${v}`)}
          onChange={(tone) => setAudio({ tone })}
        />
      </>
    ),
  };

  return (
    <section className="goal-options">
      <h3>{t('studio.opt.title')}</h3>
      <p className="muted small">{t('studio.opt.hint')}</p>
      {groups.map((group) => (
        // The text-on-picture group is the one most people leave alone.
        <details key={group} className="goal-group" open={group !== 'textOnMedia'}>
          <summary>{t(GROUP_TITLE[group])}</summary>
          <div className="goal-group-body">{body[group]}</div>
        </details>
      ))}
    </section>
  );
}
